/**
 * A player that plays on over a score that grows under it.
 *
 * A scan's pages are laid out as they are read (docs/06, "Reading a scan"), and each reading
 * so far is a new score: the player made for it is a new one, and a new player is silent.
 * Where the score is one that grows (`growing`), or the one it replaces did, and the player
 * before it was playing, the new one is played on from the same second, which is the same place in the music: the pages
 * before it are the pages that were playing. Any other new score stops, as it always has: a
 * new arrangement is other music, and nobody asked to hear it from the middle.
 */

import { useEffect, useRef } from "react";
import type { Score } from "@app/score-editor";
import type { Playback } from "@app/score-editor/react";

export function usePlayOn(playback: Playback, score: Score, growing: boolean): void {
  const heard = useRef({ score, growing });
  useEffect(() => {
    const was = heard.current;
    heard.current = { score, growing };
    if (was.score === score) return;
    // The whole, or the pages kept when the reading stops, replaces a score that grew: it
    // plays on too. Where it was is this render's, from before the old player was silenced;
    // `play` is the new player's.
    if ((growing || was.growing) && playback.playing) playback.play(playback.position);
  }, [score, growing, playback]);
}
